import dayjs from 'dayjs';
import map from 'lodash/map';
import some from 'lodash/some';
import find from 'lodash/find';
import toLower from 'lodash/toLower';
import get from 'lodash/get';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import zipObject from 'lodash/zipObject';
import isUndefined from 'lodash/isUndefined';
import includes from 'lodash/includes';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { max, filter, forEach } from 'lodash';
import { ONE_BUSINESS_DAY_IN_MS, roundEllapsedTime } from '../../utils/format.js';
import { TASK_ELLAPSED_BAR, TASK_ELLAPSED_TIME_STEP_IN_MS } from './projects.constants.js';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export function isTaskMatchEstimationFilters({ estimationFilters, estimatedTime, estimatedTimeAcceptedAt, estimatedTimeRefusedAt }) {
  const filtersToCheck = pick(
    {
      without: isUndefined(estimatedTime) || estimatedTime === 0,
      awaitingValidation: !isUndefined(estimatedTime) && estimatedTime > 0 && isEmpty(estimatedTimeAcceptedAt) && isEmpty(estimatedTimeRefusedAt),
      refused: !isEmpty(estimatedTimeRefusedAt),
      accepted: !isEmpty(estimatedTimeAcceptedAt),
    },
    ...estimationFilters,
  );

  return some(filtersToCheck, Boolean);
}

export function handleDecimalValueInput(event) {
  const { keyCode } = event;
  const authorizedControlsKeys = [8, 46, 37, 38, 39, 40];
  if (!includes(authorizedControlsKeys, keyCode) && !/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
}

export function isTaskMatchDateFilter({ dateFilter, comparatorFilter, date }) {
  if (!date) {
    return false;
  }

  const taskDate = dayjs.utc(date);
  const comparatorDate = dayjs.utc(dateFilter);

  const filtersResult = {
    before: comparatorFilter === 'lt' && taskDate.isBefore(comparatorDate, 'day'),
    at: comparatorFilter === 'eq' && taskDate.isSame(comparatorDate, 'day'),
    after: comparatorFilter === 'gt' && taskDate.isAfter(comparatorDate, 'day'),
  };

  return some(filtersResult, Boolean);
}

export function formatLabelRulesToObject(rules) {
  return zipObject(
    map(rules, 'name'),
    map(rules, (rule) => zipObject(map(rule.params, 'key'), map(rule.params, 'value'))),
  );
}

export function formatLabelRulesToArray(rules) {
  return map(rules, (ruleValues, label) => ({
    name: label,
    params: map(ruleValues, (value, key) => ({ key, value })),
  }));
}

/** Clé de fusion : trim uniquement (sensible à la casse — ex. « Projet devis » ≠ « Projet Devis »). */
export function labelMergeGroupKey(name) {
  return String(name ?? '').trim();
}

/**
 * Fusionne les labels par type + nom exact (même casse après trim). `name` / `backgroundColor` = premier enregistrement.
 */
export function mergeLabelsListForFilters(labels) {
  const byKey = new Map();
  const withIds = filter(labels || [], (label) => label?._id);
  forEach(withIds, (label) => {
    const key = `${label.type}::${labelMergeGroupKey(label.name)}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        name: label.name,
        backgroundColor: label.backgroundColor,
        type: label.type,
        ids: [label._id],
      });
      return;
    }
    byKey.get(key).ids.push(label._id);
  });
  return [...byKey.values()];
}

/**
 * Étend les `_id` sélectionnés vers tous les labels du catalogue même type + même clé de fusion
 * (`labelMergeGroupKey`). Utile quand la BDD contient des doublons de libellé (ids distincts).
 */
export function expandSelectedLabelIdsForFilter(labelIds, catalogLabels) {
  if (!labelIds?.length) {
    return [];
  }
  const labels = catalogLabels || [];
  const byId = new Map(labels.map((l) => [l._id, l]));
  const mergeKeys = new Set();
  forEach(labelIds, (id) => {
    const l = byId.get(id);
    if (l?._id) {
      mergeKeys.add(`${l.type}::${labelMergeGroupKey(l.name)}`);
    }
  });
  if (mergeKeys.size === 0) {
    return [...labelIds];
  }
  const out = new Set(labelIds);
  forEach(labels, (l) => {
    if (!l?._id) {
      return;
    }
    const key = `${l.type}::${labelMergeGroupKey(l.name)}`;
    if (mergeKeys.has(key)) {
      out.add(l._id);
    }
  });
  return [...out];
}

export function getTaskLegalMaxEllapsedTimeAllowed(estimatedTime) {
  const oneWorkDay = estimatedTime + ONE_BUSINESS_DAY_IN_MS;
  const fifthyPercent = estimatedTime * 1.5;
  return max([oneWorkDay, fifthyPercent]);
}

export function getTaskEllapsedTimeColors({ task, labels }) {
  if (!task) return '';

  const progressTotal = Math.round(((task.ellapsedTime || 0) / task.estimatedTime) * 100);
  const progressNormalPerc = Math.min(progressTotal, 100);
  const ellapsedTimeWarningThreshold = task.estimatedTime;
  const ellapsedTimeErrorThreshold = getTaskLegalMaxEllapsedTimeAllowed(task.estimatedTime);
  const isProgressAboveEstimation = task.ellapsedTime > ellapsedTimeWarningThreshold;
  const isProgressEqualToLimitAuthorizedByCgv = task.ellapsedTime > ellapsedTimeErrorThreshold;
  const isProgressAboveLimitAuthorizedByCgv = task.ellapsedTime > ellapsedTimeErrorThreshold;
  const hasMissionLabel = !!find(labels, (label) => toLower(label.name) === 'mission');
  const isTaskHasBeenEstimated = !!get(task, 'estimatedTime');
  const isTaskEstimatedTimeAlreadyAccepted = !!get(task, 'estimatedTimeAcceptedAt');
  const isTaskEstimatedTimeAlreadyRefused = !!get(task, 'estimatedTimeRefusedAt');
  const isWaitingEstimationValidation = isTaskHasBeenEstimated && !isTaskEstimatedTimeAlreadyAccepted && !isTaskEstimatedTimeAlreadyRefused;

  return {
    [TASK_ELLAPSED_BAR.WITHOUT_TIME]: progressNormalPerc === 0,
    [TASK_ELLAPSED_BAR.IN_RANGE]: progressNormalPerc > 0 && (isTaskEstimatedTimeAlreadyAccepted || isWaitingEstimationValidation || !hasMissionLabel) && !isProgressAboveEstimation,
    [TASK_ELLAPSED_BAR.OVERFLOW]:
      (isTaskEstimatedTimeAlreadyAccepted || isWaitingEstimationValidation || !hasMissionLabel) && isProgressAboveEstimation && !isProgressAboveLimitAuthorizedByCgv,
    [TASK_ELLAPSED_BAR.TOO_MUCH_OVERLFLOW]:
      ((isTaskEstimatedTimeAlreadyAccepted || isWaitingEstimationValidation) && (isProgressEqualToLimitAuthorizedByCgv || isProgressAboveLimitAuthorizedByCgv)) ||
      isTaskEstimatedTimeAlreadyRefused,
  };
}

export function getTasksFiltersChecks({ task, state }) {
  // eslint-disable-next-line max-len
  const {
    name,
    taskId,
    description,
    labelIds = [],
    kind,
    priority,
    pendingAt,
    estimatedTime,
    milestoneIds,
    estimatedTimeAcceptedAt,
    estimatedTimeRefusedAt,
    plannedAt,
    dueAt,
  } = task;
  return {
    search:
      isEmpty(state.currentSearch) ||
      name.toLowerCase().includes(state.currentSearch.toLowerCase()) ||
      taskId.toLowerCase().includes(state.currentSearch.toLowerCase()) ||
      description.toLowerCase().includes(state.currentSearch.toLowerCase()),
    labels: isEmpty(state.labelFilters) || some(labelIds, (labelId) => state.labelFilters.includes(labelId)),
    milestones: isEmpty(state.milestonesFilters) || some(milestoneIds, (milestoneId) => state.milestonesFilters.includes(milestoneId)),
    kind: isEmpty(state.kindFilters) || state.kindFilters.includes(kind),
    priority: isEmpty(state.prioritiesFilters) || state.prioritiesFilters.includes(priority),
    pendingAt: isUndefined(state.isPendingFilter) || (state.isPendingFilter === true && pendingAt !== undefined) || (state.isPendingFilter === false && pendingAt === undefined),
    estimation:
      isEmpty(state.estimationFilters) ||
      isTaskMatchEstimationFilters({
        estimationFilters: state.estimationFilters,
        estimatedTime,
        estimatedTimeAcceptedAt,
        estimatedTimeRefusedAt,
      }),
    plannedDate:
      isEmpty(state.plannedDateFilter) ||
      isTaskMatchDateFilter({
        dateFilter: state.plannedDateFilter,
        comparatorFilter: state.plannedComparatorFilter,
        date: plannedAt,
      }),
    deliveryDate:
      isEmpty(state.deliveryDateFilter) ||
      isTaskMatchDateFilter({
        dateFilter: state.deliveryDateFilter,
        comparatorFilter: state.deliveryComparatorFilter,
        date: dueAt,
      }),
  };
}

export function getTaskWorklogEllapsedTime(workLog) {
  const startedAt = get(workLog, 'startedAt', 0) ? new Date(get(workLog, 'startedAt', 0)).getTime() : new Date().getTime();
  const stoppedAt = get(workLog, 'stoppedAt') ? new Date(get(workLog, 'stoppedAt')).getTime() : new Date().getTime();
  const pauseTimeInMs = get(workLog, 'pausedAt')
    ? new Date().getTime() - new Date(get(workLog, 'pausedAt')).getTime() + get(workLog, 'pauseTimeInMs', 0)
    : get(workLog, 'pauseTimeInMs', 0);
  return stoppedAt - startedAt - pauseTimeInMs;
}

export function getWorklogsBillableTime(worklogs) {
  return worklogs.reduce((acc, worklog) => {
    const ellapsedTime = getTaskWorklogEllapsedTime(worklog);
    return acc + roundEllapsedTime(ellapsedTime, TASK_ELLAPSED_TIME_STEP_IN_MS);
  }, 0);
}

/** Au moins une date de planification (début ou fin prévue) — requis pour le Gantt. */
export function taskHasPlannedOrDueAt(task) {
  return !!(task?.plannedAt || task?.dueAt);
}

/** Tâche non archivée (les archives portent `deletedAt`). */
export function taskIsNotArchived(task) {
  return !task?.deletedAt;
}

/** Filtre OR : la tâche doit porter au moins un des `labelIds` (même logique que le Kanban côté client). */
export function taskMatchesLabelIdsFilter(task, labelIds) {
  if (!labelIds?.length) return true;
  const taskLabels = task.labelIds || [];
  return some(labelIds, (id) => includes(taskLabels, id));
}

/**
 * Filtre les tâches dont plannedAt et/ou dueAt croise la plage [rangeStart, rangeEnd]
 * (jours calendaires locaux). Ignore les tâches sans ces deux champs.
 */
export function filterTasksIntersectingDateRange(tasks, rangeStart, rangeEnd) {
  if (!rangeStart || !rangeEnd) {
    return tasks;
  }

  const rangeS = dayjs(rangeStart).startOf('day');
  const rangeE = dayjs(rangeEnd).endOf('day');

  return filter(tasks, (task) => {
    const planned = task.plannedAt ? dayjs(task.plannedAt) : null;
    const due = task.dueAt ? dayjs(task.dueAt) : null;

    if (!planned && !due) {
      return false;
    }

    if (planned && due) {
      return planned.isSameOrBefore(rangeE) && due.isSameOrAfter(rangeS);
    }
    if (planned) {
      return planned.isSameOrAfter(rangeS) && planned.isSameOrBefore(rangeE);
    }
    if (due) {
      return due.isSameOrAfter(rangeS) && due.isSameOrBefore(rangeE);
    }
    return false;
  });
}

export function getCurrentDayMaxWorkHours(date) {
  const currentDay = date ? dayjs(date) : dayjs();
  const fridayWeekDay = 5;

  return currentDay.day() === fridayWeekDay ? 7 : 8;
}
export function getInvoicableWorklogs({ workLogs, acceptedEstimatedTime }) {
  if (acceptedEstimatedTime === 0) return [];
  const legalMaxAllowedTime = getTaskLegalMaxEllapsedTimeAllowed(acceptedEstimatedTime);
  let currentAdditiveEllapsedTime = 0;

  return workLogs.reduce((acc, workLog) => {
    const workLogEllapsedTime = getTaskWorklogEllapsedTime(workLog);
    const roundedWorkLogEllapsedTime = roundEllapsedTime(workLogEllapsedTime, 900_000);
    if (currentAdditiveEllapsedTime >= legalMaxAllowedTime) return acc;
    currentAdditiveEllapsedTime += roundedWorkLogEllapsedTime;
    return [...acc, workLog];
  }, []);
}
